import PropTypes from 'prop-types'
import React, {useEffect} from 'react'
import Color from 'color'
import {getHubConfig} from './config';

const DEFAULT_THEME = {
  mainFont: "Inter, -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif",
  fontSize: "14px",
  backgroundPrimary: "#FAFBFC",
  actionPrimary: "#2563EB",
  actionLink: "#2563EB",
  backgroundNeutral: "#F8FAFC"
}

const getAdjustedColor = (base, amount = 0.2) => {
  const c = Color(base)
  const lightenAmount = c.isLight() ? amount / 8 : amount
  const adjusted = c.lighten(lightenAmount)

  // If the resulting color is too close to white, return the original color
  if (adjusted.luminosity() > 0.98) {
    return c.hex()
  }

  return adjusted.hex()
}

const generateCssVariables = (theme) => {
  const {
    mainFont,
    fontSize,
    backgroundPrimary,
    actionPrimary,
    actionLink,
    backgroundNeutral,
  } = theme

  const themeVars = {
    // Font
    '--main-font': mainFont,
    '--main-size': fontSize,

    // Base colors
    '--palette-common-white': '#ffffff',
    '--palette-common-grey-50': '#fafafa',
    '--palette-common-grey-100': '#f2f2f2',
    '--palette-common-grey-200': '#dddddd',
    '--palette-common-grey-300': '#cccccc',
    '--palette-common-grey-600': '#757575',
    '--palette-common-grey-700': '#636363',

    // Background primary shades
    '--palette-background-primary-0': backgroundPrimary,
    '--palette-background-primary-100': getAdjustedColor(backgroundPrimary, 0.2),
    '--palette-background-primary-200': getAdjustedColor(backgroundPrimary, 0.4),
    '--palette-background-primary-300': getAdjustedColor(backgroundPrimary, 0.6),

    // Background neutral shades
    '--palette-background-neutral-0': backgroundNeutral,
    '--palette-background-neutral-50': getAdjustedColor(backgroundNeutral, 0.1),
    '--palette-background-neutral-100': getAdjustedColor(backgroundNeutral, 0.2),
    '--palette-background-neutral-200': getAdjustedColor(backgroundNeutral, 0.4),
    '--palette-background-neutral-300': getAdjustedColor(backgroundNeutral, 0.6),

    // Action primary
    '--palette-action-primary-default': actionPrimary,
    '--palette-action-primary-hover': getAdjustedColor(actionPrimary, 0.4),

    // Action link
    '--palette-action-link-default': actionLink,
    '--palette-action-link-hover': getAdjustedColor(actionLink, 0.2),
    '--palette-action-link-focus': actionLink,

    // Text colors
    '--palette-text-primary': '#212121',
    '--palette-text-secondary': '#757575',
  }

  return Object.entries(themeVars)
    .map(([key, value]) => `${key}: ${value};`)
    .join('\n')
}

const ThemeProvider = ({children}) => {
  const {theme = DEFAULT_THEME} = getHubConfig();

  useEffect(() => {
    const styleTag = document.createElement('style')
    styleTag.setAttribute('data-generated-theme', 'true')

    const cssVars = generateCssVariables(theme)
    styleTag.innerHTML = `:root {\n${cssVars}\n}`

    document.head.appendChild(styleTag)

    return () => {
      document.head.removeChild(styleTag)
    }
  }, [theme])

  return <>{children}</>
}

ThemeProvider.propTypes = {
  children: PropTypes.object.isRequired
}

export default ThemeProvider
